import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { ValueFormatterService, ValueMask } from '@shared/value-formatter/value-formatter.service';
import { RequiredFieldsService } from '../../required-fields.service';
import { TranslateService } from '@ngx-translate/core';

import { BaseInputComponent } from '../base-input.component';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { FloatLabelType, MatFormField } from '@angular/material/form-field';
import { TypedFormGroup } from '@feature/forms/form-model';

type ForminputType = 'password' | 'text' | 'textarea' | 'number' | 'colorPicker' | null;

@Component({
    selector: 'wai-form-input',
    templateUrl: './form-input.component.html',
    styleUrls: ['./../form-controls.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormInputComponent extends BaseInputComponent implements AfterViewInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() public tooltip?: string;
  @Input() public autocomplete?: MatAutocomplete;
  @Input() public clearable = false;
  @Input() public floatLabel: FloatLabelType = 'auto';
  @Input() public hint: string;
  @Input() public label: string;
  @Input() public mask: ValueMask = null;
  @Input() public prefixIcon: string = null;
  @Input() public suffixIcon: string = null;
  @Input() public suffix: string = null;
  @Input() public type: ForminputType = 'text';
  @Input() public refreshAction: () => {};
  @Input() public updateOutline = false;
  @Input() public placeholder: string;
  @Input() public min: string | number;
  @Input() public max: string | number;
  @Input() public step: string | number;
  @ViewChild(MatFormField) public formField: MatFormField;
  @ViewChild('colorPickerInput') public colorPickerInput: ElementRef<HTMLInputElement>;
  @Input() public isDisabled: boolean;
  @Output() public inputChange = new EventEmitter<string>();
  public rawValue: string = '';
  private _controlName: string | null = null;

  constructor(
    private valueFormatterService: ValueFormatterService,
    protected changeDetectorRef: ChangeDetectorRef,
    private requiredFieldsService: RequiredFieldsService,
    private translateService: TranslateService,
  ) {
    super(changeDetectorRef);
  }

  public onRefreshClickedAction(event: Event): void {
    event.stopPropagation();
    if (this.refreshAction) {
      this.refreshAction();
    }
  }

  public ngAfterViewInit(): void {
    if (this.isDisabled) {
      this.control.disable();
    }
    if (this.updateOutline) {
      // Use explicitly in outlined inputs inside dialogs to fix floating label issue
      // WAI-772 / https://github.com/angular/components/issues/15027
      // TODO: updateOutlineGap() method removed in Angular Material 17
      // setTimeout(() => {
      //   this.formField.updateOutlineGap();
      // }, 100);
    }

    this.changeDetectorRef.detectChanges();
  }

  public onKeyPressed(event: KeyboardEvent): boolean {
    return this.valueFormatterService.isCharAllowed(event.key, this.mask);
  }

  public onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.inputChange.emit(inputElement.value);
  }

  public getDisplayLabel(): string {
    if (!this.label) {
      return '';
    }

    try {
      const translatedLabel = this.translateService.instant(this.label);

      // Only check for required fields if we have a control
      if (this.control) {
        const controlName = this.getControlName();
        if (
          controlName &&
          this.requiredFieldsService.isFieldRequired(controlName) &&
          !this.isRequired(this.control)
        ) {
          return `${translatedLabel} *`;
        }
      }

      return translatedLabel;
    } catch (error) {
      // Fallback to original label if translation fails
      console.warn('Translation error for label:', this.label, error);
      return this.label;
    }
  }

  private getControlName(): string | null {
    // Cache the control name for performance
    if (this._controlName !== null) {
      return this._controlName;
    }

    if (!this.control || !this.control.parent) {
      return null;
    }

    // Find the control name by comparing control references
    const formGroup = this.control.parent;
    for (const [key, control] of Object.entries(formGroup.controls)) {
      if (control === this.control) {
        this._controlName = key;
        return key;
      }
    }
    return null;
  }

  public openColorPicker(): void {
    if (this.type === 'colorPicker' && this.colorPickerInput?.nativeElement) {
      this.colorPickerInput.nativeElement.click();
    }
  }

  public onColorPickerChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const colorValue = inputElement.value;
    if (this.control) {
      this.control.setValue(colorValue, { emitEvent: true });
      this.changeDetectorRef.markForCheck();
    }
  }

  public onColorInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let colorValue = inputElement.value.trim();
    
    // Normalize hex color - ensure it starts with #
    if (colorValue && !colorValue.startsWith('#')) {
      colorValue = '#' + colorValue;
    }
    
    // Validate hex color format
    if (this.isValidHexColor(colorValue)) {
      if (this.control) {
        this.control.setValue(colorValue, { emitEvent: true });
        this.changeDetectorRef.markForCheck();
      }
    }
    this.onInput(event);
  }

  private isValidHexColor(color: string): boolean {
    if (!color) {
      return false;
    }
    // Check if it's a valid hex color (with or without #)
    const hexColorRegex = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexColorRegex.test(color);
  }
}
